import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Tooltip,
  useToast,
  Badge,
} from "@chakra-ui/react";
import { FaMicrophone } from "react-icons/fa";
import { useExternalRecording } from "../pages/AudioRecorder/hooks/useExternalRecording";
import { trackUserEvent } from "../utils/userEventTracker";
import { beginActivity, endActivity } from "../utils/activityTracker";
import { runLiveAiAnalysis } from "../pages/AudioRecorder/utils/liveTranscriptAiAnalysis";
import {
  saveTranscriptBackupFromRecorderMessage,
  endBackupSession,
  getBackupStatus,
} from "../pages/AudioRecorder/utils/transcriptionBackupUtils";
import { appendLiveTranscriptEntry } from "../pages/AudioRecorder/utils/databaseUtils";
import { getSupabase } from "./supabaseClient";
import {
  parseAudioPeriodicBackupSettings,
  ExternalRecordingService,
} from "../pages/AudioRecorder/services/externalRecordingService";
import { supabasePublic, supabaseSchema } from "./supabaseClient";
import { bucketName, supabaseUrl } from "./env";

// Pull date_ from patientData regardless of whether it's an array (e.g. [row]) or a single row object.
const extractVisitDate = (data) =>
  data?.[0]?.date_ || data?.date_ || "";

// Same tolerance for the row's schema tag: a chart opened through the
// cross-schema Providers dropdown lives outside the viewer's home schema, and
// that is the schema the recorder must write `more_audios` into.
const extractRowSchema = (data) =>
  data?.[0]?._schema || data?._schema || "";

/**
 * Recording Bubble Widget
 * 
 * A floating microphone button that opens an external recording window.
 * Positioned to the left of the ChatBubble.
 * 
 * When chat is expanded, this bubble remains visible to allow
 * quick access to recording while using the chat.
 */
// Leejoe 2026-06-29 — `hidden` prop. The top-bar Record Visit chip in
// DoctorPortalBar has replaced this floating-mic UI, but we still need
// RecordingBubble mounted so its window-event listeners (recordingBubbleOpen,
// patientContextUpdate, periodic backup messages, popup state sync, etc.)
// keep firing. Pass `hidden` to render the component invisibly but keep
// all useEffects + state machinery alive.
const RecordingBubble = ({
  currentPatientId,
  currentPatientData,
  // Schema the open chart writes everything else with (MedStudentClinicPage's
  // `chartDocSchema`). Wins over the row's raw `_schema` because it has already
  // been through the provider allow-list gate. Optional: callers without chart
  // context fall back to the row tag, then to the viewer's home schema.
  chartSchema = null,
  isExpanded = false,
  onToggle = () => {},
  hidden = false,
}) => {
  const [patientId, setPatientId] = useState(currentPatientId || null);
  const [patientData, setPatientData] = useState(currentPatientData || null);
  const [isRecordingOpen, setIsRecordingOpen] = useState(false);
  const [dbRole, setDbRole] = useState("");
  const [dbCopilotEnabled, setDbCopilotEnabled] = useState(false);
  // Track actual recording state from external window: 'prompt', 'opened', 'recording', 'paused', 'stopped'
  const [recordingState, setRecordingState] = useState('prompt');
  const windowRef = useRef(null);
  const toast = useToast();
  const lastPatientIdRef = useRef(null);

  // Get patient name helper
  const getPatientName = useCallback((data) => {
    return data?.[0]?.patient_name || 
           data?.patient_name || 
           data?.name || 
           "Patient";
  }, []);

  // Listen for patientContextUpdate events from PatientPage
  useEffect(() => {
    const handlePatientContextUpdate = (e) => {
      const { patientId: newPatientId, patientData: newPatientData } = e.detail || {};
      if (newPatientId && newPatientId !== lastPatientIdRef.current) {
        lastPatientIdRef.current = newPatientId;
        setPatientId(newPatientId);
        setPatientData(newPatientData || null);
        console.log(`[RecordingBubble] Patient context updated: ${getPatientName(newPatientData)}`);
      }
    };

    window.addEventListener("patientContextUpdate", handlePatientContextUpdate);
    return () => window.removeEventListener("patientContextUpdate", handlePatientContextUpdate);
  }, [getPatientName]);

  // Sync patient context when props change.
  //
  // Parent (MedStudentClinicPage) usually renders the uuid from the URL BEFORE
  // visit.patient finishes loading from Supabase, so we get two updates:
  //   (1) id=<uuid>, data=null   ← mount
  //   (2) id=<uuid>, data={row}  ← row arrives
  //
  // The previous version gated BOTH setters on id-change, so step (2) was a
  // no-op and patientData stayed null forever. getPatientName(null) then fell
  // through to the literal "Patient" fallback, which is what the recorder
  // window header was showing. Split the sync: id resets are still gated
  // (cheap + lets us reset lastPatientIdRef), but patientData mirrors the
  // latest prop whenever it's non-null.
  useEffect(() => {
    if (currentPatientId && currentPatientId !== lastPatientIdRef.current) {
      lastPatientIdRef.current = currentPatientId;
      setPatientId(currentPatientId);
    }
    if (currentPatientData) {
      setPatientData(currentPatientData);
    }
  }, [currentPatientId, currentPatientData]);

  // Load authenticated user's role + copilot flag directly from users table.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || !mounted) return;
        const { data: userData } = await supabase
          .from("users")
          .select("role, copilot_enabled")
          .eq("id", user.id)
          .single();
        if (!mounted) return;
        const roleValue = String(userData?.role || "");
        setDbRole(roleValue);
        // Stripe paywall removed (2026-06-25, Brett). Every user gets the
        // premium editor + Copilot unconditionally.
        setDbCopilotEnabled(true);
      } catch (e) {
        // keep fallback behavior
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Listen for recording state changes from external window
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data) return;

      // Leejoe's "Make this my note" — recorder window sends the SOAP report,
      // we inject it into the active Quill editor on the patient chart.
      // Fan-out via CustomEvent so whichever component owns the Quill instance
      // can listen without RecordingBubble needing direct refs to it.
      if (data.type === "INSERT_REPORT_AS_NOTE") {
        const plain = data.payload?.plain || '';
        if (plain) {
          window.dispatchEvent(new CustomEvent('vmed:insertReportAsNote', {
            detail: { plain, source: 'externalRecorder' }
          }));
          console.log('[RecordingBubble] INSERT_REPORT_AS_NOTE fanned out as vmed:insertReportAsNote');
        }
        return;
      }

      // Approach B convergence: recorder popup wants the chart to open
      // Process Forms with the latest recording. Fan-out as a CustomEvent so
      // the active clinic page (MedStudentClinicPage) can react without
      // RecordingBubble needing direct refs to the patient row or modal state.
      if (data.type === "OPEN_PROCESS_FOR_RECORDING") {
        const sessionId = data.payload?.sessionId || data.sessionId || null;
        const autoGenerate = !!data.payload?.autoGenerate;
        window.dispatchEvent(new CustomEvent('vmed:openProcessForRecording', {
          detail: { sessionId, autoGenerate, source: 'externalRecorder' }
        }));
        console.log('[RecordingBubble] OPEN_PROCESS_FOR_RECORDING fanned out as vmed:openProcessForRecording', { sessionId, autoGenerate });
        return;
      }

      if (data.type === "LIVE_TRANSCRIPT_UPDATE") {
        // Bridge the popup's live transcript to a CustomEvent on the parent
        // window — MedStudentClinicPage mounts RecordingBubble but NOT
        // AudioRecorder, so listeners on `liveTranscriptUpdate` (right-bar
        // Live Transcript tab, Master Note fallback) would otherwise miss
        // updates. Task #280: tag frames with the recording's patient so
        // downstream consumers can refuse text from a different chart.
        if (typeof window !== "undefined" && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("liveTranscriptUpdate", {
              detail: {
                text: data.text || "",
                timestamp: data.timestamp || new Date().toISOString(),
                patientId: data.patientId || patientId || null,
              },
            }),
          );
        }
        return;
      }

      if (data.type === "REQUEST_PATIENT_SYNC") {
        console.log("🎤 [RecordingBubble] Received REQUEST_PATIENT_SYNC from popup. Current bubble context:", {
          patientId,
          patientData,
        });
        if (event.source && typeof event.source.postMessage === "function") {
          try {
            event.source.postMessage({
              type: "PARENT_PATIENT_CONTEXT",
              patientId: patientId || "",
              patientName: getPatientName(patientData) || "",
              ehrId: patientData?.ehr_id || patientData?.[0]?.ehr_id || "",
            }, "*");
            console.log("🎤 [RecordingBubble] Replied to REQUEST_PATIENT_SYNC with PARENT_PATIENT_CONTEXT.");
          } catch (err) {
            console.error("🎤 [RecordingBubble] Error posting PARENT_PATIENT_CONTEXT back to popup:", err);
          }
        }
        return;
      }

      // Live-transcript backups (transcription-backups bucket) are owned by
      // useWindowMessaging when AudioRecorder is mounted. On pages that only
      // mount this bubble nothing else persists the transcript, so do it here.
      const audioRecorderOwnsBackups = !!window.__notesAiAudioRecorderMounted;
      if (
        !audioRecorderOwnsBackups &&
        (data.type === "AUDIO_RECORDING_BACKUP" ||
          data.type === "RECORDING_PAUSED" ||
          data.type === "RECORDING_STOPPED" ||
          data.type === "RECORDING_ABRUPTLY_CLOSED")
      ) {
        const transcriptForBackup =
          data.liveTranscriptText ?? data.transcriptText ?? data.finalTranscript;
        const recordingState = {
          AUDIO_RECORDING_BACKUP: "recording",
          RECORDING_PAUSED: "paused",
          RECORDING_STOPPED: "stopped",
          RECORDING_ABRUPTLY_CLOSED: "abruptly_closed",
        }[data.type];
        if (transcriptForBackup && String(transcriptForBackup).trim()) {
          const visitDateForBackup =
            data?.patientScheduledDate ||
            data?.scheduledDate ||
            extractVisitDate(patientData);
          saveTranscriptBackupFromRecorderMessage(data, {
            visitDate: visitDateForBackup,
            recordingState,
            source: "recording_bubble",
          })
            .then((res) => {
              if (!res?.success && !res?.skipped) {
                console.warn(
                  "[RecordingBubble] Transcript backup failed:",
                  res?.error,
                );
              }
              if (
                (data.type === "RECORDING_STOPPED" ||
                  data.type === "RECORDING_ABRUPTLY_CLOSED") &&
                getBackupStatus()?.sessionId === data.sessionId
              ) {
                endBackupSession();
              }
            })
            .catch((err) => {
              console.warn(
                "[RecordingBubble] Transcript backup error:",
                err?.message || err,
              );
            });
        }
      }

      // Periodic backup messages from the recording window — upload each chunk to
      // the backup-audio bucket and prune older files so only `keep_count` remain.
      // Without this, the recording window's postMessage falls into the void.
      if (data.type === "AUDIO_RECORDING_BACKUP") {
        const visitDate =
          data?.patientScheduledDate ||
          data?.scheduledDate ||
          extractVisitDate(patientData);
        ExternalRecordingService.processPeriodicBackup(data, {
          bucketName,
          supabaseUrl,
          state: { date_: visitDate },
        }).catch((err) => {
          console.warn(
            "[RecordingBubble] Periodic backup processing failed:",
            err?.message || err,
          );
        });
        return;
      }

      // Manual "Save Live Transcript" from the popup. AudioRecorder owns this
      // when mounted; otherwise the request had no handler and the popup's
      // spinner never resolved.
      if (data.type === "SAVE_LIVE_TRANSCRIPT" && !window.__notesAiAudioRecorderMounted) {
        const popupWindow = event.source;
        const reply = (payload) => {
          if (popupWindow && typeof popupWindow.postMessage === "function") {
            popupWindow.postMessage(
              { type: "LIVE_TRANSCRIPT_SAVED", sessionId: data.sessionId || null, ...payload },
              "*",
            );
          }
        };
        if (data.alreadySaved) {
          window.dispatchEvent(new CustomEvent("transcriptionSaved"));
          reply({ success: true });
          return;
        }
        appendLiveTranscriptEntry({
          patientId: data.patientId || patientId,
          patientName: data.patientName || getPatientName(patientData) || "",
          transcriptText: data.transcriptText || "",
          duration: data.duration || 0,
          audioUrl: data.audioUrl || "",
          sessionId: data.sessionId || null,
        })
          .then((res) => reply({ success: !!res.success, error: res.error }))
          .catch((err) => reply({ success: false, error: err?.message || "Save failed" }));
        return;
      }

      if (data.type === "AUDIO_SAVED_SUCCESS") {
        const savedPatientId = data.patientId || patientId;
        const timestamp = new Date().toISOString();

        // The popup appends the live transcript row itself; when it reports
        // that write failed and no AudioRecorder is mounted to retry, retry
        // here (idempotent per session_id).
        if (
          !window.__notesAiAudioRecorderMounted &&
          data.liveTranscription &&
          data.liveTranscriptSaved === false &&
          (data.liveTranscriptText || "").trim() &&
          savedPatientId
        ) {
          const popupWindow = event.source;
          appendLiveTranscriptEntry({
            patientId: savedPatientId,
            patientName: data.patientName || getPatientName(patientData) || "",
            transcriptText: data.liveTranscriptText,
            duration: data.duration || 0,
            audioUrl: data.finalAudioUrl || data.audioUrl || "",
            sessionId: data.sessionId || null,
          })
            .then((res) => {
              console.log("[RecordingBubble] Live transcript fallback save:", res);
              if (popupWindow && typeof popupWindow.postMessage === "function") {
                popupWindow.postMessage(
                  {
                    type: "LIVE_TRANSCRIPT_SAVED",
                    success: !!res.success,
                    error: res.error,
                    autoSaved: true,
                    sessionId: data.sessionId || null,
                  },
                  "*",
                );
              }
            })
            .catch((err) => {
              console.warn(
                "[RecordingBubble] Live transcript fallback save error:",
                err?.message || err,
              );
            });
        }

        // After a successful save, prune the backup-audio bucket down to the latest
        // `keep_count` valid recordings per schema_settings.audio_periodic_backup.
        if (savedPatientId) {
          ExternalRecordingService.cleanupTemporaryBackups(
            savedPatientId,
            data.finalAudioUrl || data.audioUrl || null,
          ).catch((err) => {
            console.warn(
              "[RecordingBubble] Backup cleanup after save failed:",
              err?.message || err,
            );
          });
          ExternalRecordingService.clearRecoveryBackup(savedPatientId);
        }

        try {
          localStorage.setItem("patientUpdated", timestamp);
          if (savedPatientId) {
            localStorage.setItem(`patientUpdated_${savedPatientId}`, timestamp);
          }
        } catch (error) {
          console.warn("[RecordingBubble] Failed to persist patient update flag:", error);
        }

        window.dispatchEvent(
          new CustomEvent("audioRecordingComplete", {
            detail: {
              success: true,
              patientId: savedPatientId,
              audioUrl: data.finalAudioUrl || data.audioUrl || null,
              timestamp,
              source: "RecordingBubble",
            },
          }),
        );

        window.dispatchEvent(
          new CustomEvent("patientDataRefresh", {
            detail: {
              patientId: savedPatientId,
              timestamp,
              source: "RecordingBubble",
            },
          }),
        );

        const audioDurationMs =
          typeof data.duration === "number" ? data.duration * 1000 : null;
        trackUserEvent("audio_record", {
          duration_ms: audioDurationMs,
          metadata: {
            source: "external_recording",
            patient_id: savedPatientId,
            was_direct_upload: !!data.wasDirectUpload,
            session_id: data.sessionId || null,
          },
        });
        if (data.liveTranscription && (data.liveTranscriptText || "").trim()) {
          trackUserEvent("transcript_complete", {
            duration_ms: audioDurationMs,
            metadata: {
              source: "live_transcript",
              patient_id: savedPatientId,
              session_id: data.sessionId || null,
            },
          });
        }

        // If AudioRecorder isn't mounted in this page, run AI after Stop & Save
        // to populate clinical tabs in the external window.
        try {
          const audioRecorderMounted = !!window.__notesAiAudioRecorderMounted;
          const t = (data.liveTranscriptText || "").trim();
          if (!audioRecorderMounted && t) {
            console.log("[RECAP DEBUG] RecordingBubble running AI on save", {
              transcriptLength: t.length,
              sessionId: data.sessionId || null,
              patientId: data.patientId || patientId || null,
            });
            void runLiveAiAnalysis(
              (msg) => {
                if (windowRef.current && !windowRef.current.closed) {
                  windowRef.current.postMessage(msg, "*");
                }
              },
              t,
              { clinicalEnabled: true, modelChoice: 1 },
            ).catch((e) => {
              console.warn(
                "[RECAP DEBUG] RecordingBubble AI failed:",
                e?.message,
              );
            });
          }
        } catch (e) {
          // ignore
        }
      }

      if (data.type === "RECORDING_STATE_CHANGE" || data.type === "RECORDING_ABRUPTLY_CLOSED") {
        // Handle both normal state changes and abrupt close (e.g., window closed via X button or tab/window closed)
        const newState = data.type === "RECORDING_ABRUPTLY_CLOSED" ? "prompt" : data.state;
        
        // Check if patient context matches (patient-aware sync)
        const targetPatientId = data.patientId || patientId;
        const isCurrentPatient = targetPatientId && patientId && String(targetPatientId) === String(patientId);
        
        const effectiveState = isCurrentPatient ? newState : 'prompt';
        console.log("[RecordingBubble] Recording state changed:", effectiveState, `(triggered by ${data.type}, targetPatientId: ${targetPatientId}, currentPatientId: ${patientId})`);
        setRecordingState(effectiveState);
        
        // Also update isRecordingOpen based on state - window is open for all states except prompt/closed
        if (effectiveState === 'prompt' || effectiveState === 'closed') {
          setIsRecordingOpen(false);
        } else {
          setIsRecordingOpen(true);
        }
        
        // Persist state to localStorage so other components can sync
        try {
          if (isCurrentPatient) {
            localStorage.setItem("audioRecorderRecordingState", newState);
            localStorage.setItem("audioRecorderPatientId", targetPatientId || "");
          }
        } catch (error) {
          console.warn("[RecordingBubble] Failed to persist recording state:", error);
        }
        
        // Dispatch custom events for other components to react
        window.dispatchEvent(new CustomEvent('recordingBubbleStateChange', {
          detail: { state: effectiveState, source: 'externalWindow' }
        }));
        // Also dispatch recordingComponentStateChange for AudioRecorder and other components
        window.dispatchEvent(new CustomEvent('recordingComponentStateChange', {
          detail: { state: effectiveState, source: 'RecordingBubble' }
        }));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [patientId, patientData, getPatientName]);



  // Also listen for custom events from other recording components (e.g., AudioRecorder)
  useEffect(() => {
    const handleExternalStateChange = (event) => {
      // Skip if this event came from us (prevent infinite loops)
      if (event.detail?.source === 'externalWindow') return;
      
      console.log("[RecordingBubble] Received state change from another component:", event.detail?.state);
      if (event.detail?.state) {
        setRecordingState(event.detail.state);
        // Handle both 'prompt'/'closed' (no active recording) and other states
        if (event.detail.state === 'prompt' || event.detail.state === 'closed') {
          setIsRecordingOpen(false);
        } else {
          setIsRecordingOpen(true);
        }

      }
    };

    window.addEventListener('recordingComponentStateChange', handleExternalStateChange);
    return () => window.removeEventListener('recordingComponentStateChange', handleExternalStateChange);
  }, []);

  // Sync with localStorage on mount and periodically (for cross-tab synchronization)
  useEffect(() => {
    const syncFromLocalStorage = () => {
      try {
        const savedState = localStorage.getItem("audioRecorderRecordingState");
        const savedPatientId = localStorage.getItem("audioRecorderPatientId");
        
        const isCurrentPatient = savedPatientId && patientId && String(savedPatientId) === String(patientId);
        const effectiveState = isCurrentPatient && savedState ? savedState : 'prompt';
        
        if (effectiveState !== recordingState) {
          console.log("[RecordingBubble] Syncing state from localStorage:", effectiveState, "savedPatientId:", savedPatientId, "currentPatientId:", patientId);
          setRecordingState(effectiveState);
          if (effectiveState === 'prompt') {
            setIsRecordingOpen(false);
          } else {
            setIsRecordingOpen(true);
          }
        }
      } catch (error) {
        console.warn("[RecordingBubble] Failed to read recording state from localStorage:", error);
      }
    };

    // Initial sync
    syncFromLocalStorage();

    // Sync on storage event (when localStorage changes in another tab/window)
    const handleStorageChange = (event) => {
      if (event.key === "audioRecorderRecordingState" || event.key === "audioRecorderPatientId") {
        const savedState = localStorage.getItem("audioRecorderRecordingState");
        const savedPatientId = localStorage.getItem("audioRecorderPatientId");
        
        const isCurrentPatient = savedPatientId && patientId && String(savedPatientId) === String(patientId);
        const effectiveState = isCurrentPatient && savedState ? savedState : 'prompt';
        
        console.log("[RecordingBubble] Storage event detected, effectiveState:", effectiveState);
        setRecordingState(effectiveState);
        if (effectiveState === 'prompt') {
          setIsRecordingOpen(false);
        } else {
          setIsRecordingOpen(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [patientId, recordingState]);

  // Pull audio_periodic_backup.interval_seconds from schema_settings so periodic backups
  // honor the per-schema setting rather than a hardcoded interval.
  const [audioBackupIntervalMs, setAudioBackupIntervalMs] = useState(20000);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const schema = (supabaseSchema || "").includes(",")
          ? supabaseSchema.split(",")[0].trim()
          : supabaseSchema;
        if (!schema || schema === "public") return;
        const client = await supabasePublic();
        const { data } = await client
          .from("schema_settings")
          .select("audio_periodic_backup")
          .eq("schema_name", schema)
          .maybeSingle();
        const parsed = parseAudioPeriodicBackupSettings(
          data?.audio_periodic_backup,
        );
        if (!cancelled) setAudioBackupIntervalMs(parsed.intervalMs);
      } catch (err) {
        console.warn(
          "[RecordingBubble] Could not load audio_periodic_backup settings:",
          err?.message || err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // Use the external recording hook
  const fallbackRoleFromStorage = (() => {
    try {
      return (
        localStorage.getItem("userRole") ||
        localStorage.getItem("role") ||
        ""
      );
    } catch {
      return "";
    }
  })();
  const resolvedUserRole =
    dbRole ||
    currentPatientData?.role ||
    patientData?.role ||
    currentPatientData?.[0]?.role ||
    fallbackRoleFromStorage ||
    "";
  const roleTokens = String(resolvedUserRole || "")
    .toLowerCase()
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);

  const { generateExternalHTML, getDynamicWindowSize } = useExternalRecording({
    // date_ MUST come from the visit row, not the wall clock — the upload path
    // ({schema}/{year}/{date_}/...) is what places audios under the visit's folder.
    state: patientId
      ? {
          patient_id: patientId,
          date_:
            extractVisitDate(patientData) ||
            new Date().toISOString().split("T")[0],
          // The row's own schema — without it the popup fell back to the
          // viewer's home schema and a cross-schema chart's recording was
          // PATCHed against a uuid that doesn't exist there, failing with
          // "Patient record not found".
          _schema: extractRowSchema(patientData),
        }
      : {},
    chartSchema,
    refId: patientId,
    patientId: patientId,
    patientName: getPatientName(patientData),
    ehrId: patientData?.ehr_id || patientData?.[0]?.ehr_id || null,
    liveTranscription: true,
    userRole: resolvedUserRole,
    copilotEnabled: dbCopilotEnabled,
    preferredMicDeviceId: "",
    aiRecapDefault: true,
    audioBackupIntervalMs,
  });

  // Handle popup blocked
  const handlePopupBlocked = useCallback(() => {
    toast({
      title: "Popup blocked",
      description: "Please allow popups for this site to use the recording feature.",
      status: "warning",
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  // Handle external window close
  const handleExternalClose = useCallback(() => {
    if (windowRef.current) {
      windowRef.current = null;
    }
    setIsRecordingOpen(false);
    setRecordingState('closed'); // Reset to closed state when window is closed (consistent with AudioRecorder)
  }, []);

  // Open the external recording window
  const openRecordingWindow = useCallback(() => {
    if (!patientId) {
      toast({
        title: "No patient selected",
        description: "Please select a patient before starting a recording.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // If window already exists, just focus it
    if (windowRef.current && !windowRef.current.closed) {
      console.log("[RecordingBubble] Focusing existing external window");
      windowRef.current.focus();
      // Update state to reflect window is open
      setIsRecordingOpen(true);
      // Note: The external window will send its actual state via postMessage when focused
      return;
    }

    try {
      console.log("[RecordingBubble] Opening external recording window...");

      const htmlContent = generateExternalHTML();
      const { width, height } = getDynamicWindowSize();

      const newWindow = window.open(
        "",
        "externalRecording",
        `width=${width},height=${height},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no,titlebar=yes,top=0,left=0`
      );

      if (newWindow) {
        console.log("[RecordingBubble] External window opened successfully");
        
        // If window is already initialized and running, do not overwrite the document!
        if (newWindow.__externalRecorderInitialized) {
          console.log("[RecordingBubble] External window already initialized, focusing and skipping document write");
          newWindow.focus();
          windowRef.current = newWindow;
          setIsRecordingOpen(true);
          
          // Try to sync with existing state from localStorage immediately
          try {
            const savedState = localStorage.getItem("audioRecorderRecordingState");
            const savedPatientId = localStorage.getItem("audioRecorderPatientId");
            if (savedState && savedPatientId && String(savedPatientId) === String(patientId)) {
              setRecordingState(savedState);
            }
          } catch (e) {}

          const checkClosed = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(checkClosed);
              console.log("[RecordingBubble] Reused external window closed detected");
              if (windowRef.current) {
                windowRef.current = null;
              }
              setIsRecordingOpen(false);
              setRecordingState('closed');
            }
          }, 500);
          return;
        }

        newWindow.document.write(htmlContent);
        newWindow.document.close();
        newWindow.__externalRecorderInitialized = true;
        
        windowRef.current = newWindow;
        setIsRecordingOpen(true);
        setRecordingState('opened'); // Window is now open

        // Monitor window close directly with inline state reset
        const checkClosed = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(checkClosed);
            console.log("[RecordingBubble] External window closed detected");
            if (windowRef.current) {
              windowRef.current = null;
            }
            setIsRecordingOpen(false);
            setRecordingState('closed'); // Reset state when window closes (consistent with AudioRecorder)
          }
        }, 500); // Check more frequently (500ms)

        // No periodic focusing - window only comes to front when bubble is clicked
      } else {
        console.log("[RecordingBubble] Window.open returned null - popup blocked");
        handlePopupBlocked();
      }
    } catch (error) {
      console.error("[RecordingBubble] Error opening external window:", error);
      handlePopupBlocked();
    }
  }, [patientId, generateExternalHTML, getDynamicWindowSize, handleExternalClose, handlePopupBlocked, toast, getPatientName]);

  // Allow other UI entry points to reuse the same bubble start logic.
  useEffect(() => {
    const handleOpenFromExternalTrigger = (event) => {
      const detail =
        event?.detail && typeof event.detail === "object" ? event.detail : null;

      // `recordingBubbleOpen` is a BROADCAST — every mounted bubble hears it,
      // and more than one can be mounted (a page with its own bubble plus the
      // global BubbleContainer). A bubble that doesn't know the patient must
      // not answer for one that does: it used to toast "No patient selected"
      // over a recorder another instance had just opened correctly.
      //
      // So: claim the event only when we can actually serve it, and let a
      // patient-less bubble wait a tick. Every synchronous listener has run by
      // the time the timeout fires, so if nobody claimed it the user still gets
      // told — regardless of the order the listeners were registered in.
      if (!patientId) {
        setTimeout(() => {
          if (!detail || !detail.handled) openRecordingWindow();
        }, 0);
        return;
      }

      if (detail) detail.handled = true;
      openRecordingWindow();
    };

    window.addEventListener("recordingBubbleOpen", handleOpenFromExternalTrigger);
    return () => {
      window.removeEventListener(
        "recordingBubbleOpen",
        handleOpenFromExternalTrigger,
      );
    };
  }, [openRecordingWindow, patientId]);

  // Bubble size - consistent whether expanded or not
  const bubbleSize = "50px";

  // Prevent drag event from firing on bubble click
  const handleBubbleMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Leejoe 2026-06-29 — broadcast every recordingState/isRecordingOpen
  // transition as a `vmai:recording-state` window event. The new top-bar
  // Record Visit chip in DoctorPortalBar listens for this so its label
  // can flip between "Record Visit" / "Recording…" / "Paused" / "Open
  // Recorder" without RecordingBubble needing a direct prop wiring into
  // the doctor portal. Single useEffect on state+isOpen avoids adding a
  // dispatch call to all 14+ existing setRecordingState sites.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('vmai:recording-state', {
        detail: { recordingState, isRecordingOpen },
      }));
    } catch (_) { /* non-fatal */ }
    // Live Activity: "recording" while the external recorder is capturing/paused.
    if (recordingState === 'recording' || recordingState === 'paused') beginActivity('recording');
    else endActivity('recording');
    return () => endActivity('recording');
  }, [recordingState, isRecordingOpen]);

  // Determine tooltip and visual state based on recordingState
  // Handles both RecordingBubble's states ('prompt', 'opened', 'recording', 'paused', 'stopped')
  // and AudioRecorder's state ('closed').
  //
  // Leejoe 2026-06-29 — reworded "Recording Window Open" (which read like
  // a label of state, not an actionable hint) to "Click to bring recorder
  // to front." The same intent shapes the new top-bar Record Visit chip:
  // first click opens, subsequent clicks re-focus.
  const getTooltipLabel = () => {
    switch (recordingState) {
      case 'recording':
        return "Recording — click to focus recorder";
      case 'paused':
        return "Paused — click to focus recorder";
      case 'opened':
        return "Click to bring recorder to front";
      case 'stopped':
        return "Recording saved — click to reopen";
      case 'closed':  // AudioRecorder uses 'closed'
        return "Click to open recorder";
      default:
        return isRecordingOpen
          ? "Click to bring recorder to front"
          : "Click to open recorder";
    }
  };

  // Determine bubble color based on recordingState
  const getBubbleColor = () => {
    switch (recordingState) {
      case 'recording':
        return "red.600"; // Darker red when actively recording
      case 'paused':
        return "orange.500"; // Orange when paused
      case 'stopped':
        return "green.500"; // Green when recording is saved
      case 'closed':  // AudioRecorder uses 'closed'
      case 'prompt':  // Initial state
        return isRecordingOpen ? "red.500" : "red.500";
      default:
        return isRecordingOpen ? "red.500" : "red.500";
    }
  };

  // Determine if we should show the pulse animation (actively recording)
  const showRecordingPulse = recordingState === 'recording';
  
  // Determine if we should show the patient indicator (only when not recording)
  // Handle both 'prompt' (RecordingBubble) and 'closed' (AudioRecorder) states
  const showPatientIndicator = patientId && !isExpanded && 
    (recordingState === 'prompt' || recordingState === 'closed');

  // Hidden mode: render an empty span so the component stays mounted
  // (all event listeners + window/popup wiring keeps firing) but the
  // visible bubble is gone. Top-bar Record Visit chip in DoctorPortalBar
  // dispatches recordingBubbleOpen + drives the same openRecordingWindow.
  if (hidden) {
    return <span aria-hidden="true" style={{ display: 'none' }} />;
  }

  return (
    <Tooltip
      label={getTooltipLabel()}
      placement="top"
      hasArrow
    >
      <Box
        position="relative"
        width={bubbleSize}
        height={bubbleSize}
        borderRadius="full"
        bg={getBubbleColor()}
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
        transition="all 0.3s ease"
        cursor="pointer"
        _hover={{
          bg: recordingState === 'recording' ? "red.700" : recordingState === 'paused' ? "orange.600" : "red.600",
          transform: "scale(1.05)",
          boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)",
        }}
        onClick={openRecordingWindow}
        onMouseDown={handleBubbleMouseDown}
        flexShrink={0}
      >
        <FaMicrophone size={20} />
        
        {/* Recording indicator dot with pulse animation */}
        {showRecordingPulse && (
          <Box
            position="absolute"
            top="-2px"
            right="-2px"
            width="12px"
            height="12px"
            borderRadius="full"
            bg="white"
            border="2px solid"
            borderColor="red.500"
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width="6px"
              height="6px"
              borderRadius="full"
              bg="red.500"
              animation="pulse 1.5s infinite"
              sx={{
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          </Box>
        )}

        {/* Paused indicator - static dot (no animation) */}
        {recordingState === 'paused' && (
          <Box
            position="absolute"
            top="-2px"
            right="-2px"
            width="12px"
            height="12px"
            borderRadius="full"
            bg="orange.400"
            border="2px solid white"
          />
        )}

        {/* Stopped/Saved indicator - green checkmark */}
        {recordingState === 'stopped' && (
          <Box
            position="absolute"
            top="-2px"
            right="-2px"
            width="12px"
            height="12px"
            borderRadius="full"
            bg="green.400"
            border="2px solid white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize="8px"
            fontWeight="bold"
          >
            ✓
          </Box>
        )}

        {/* Patient indicator badge when patient is selected and not recording */}
        {showPatientIndicator && (
          <Badge
            position="absolute"
            bottom="-4px"
            right="-4px"
            colorScheme="green"
            borderRadius="full"
            fontSize="8px"
            paddingX="4px"
          >
            <Box as="span" width="6px" height="6px" borderRadius="full" bg="green.400" display="inline-block" mr="2px" />
          </Badge>
        )}
      </Box>
    </Tooltip>
  );
};

export default RecordingBubble;